// qs-useDeleteMode.ts
import { useModeManager } from '@/composables/qs-useModeManager'
import { useHistory } from '@/composables/qs-useHistory'
import { useCanvasStore } from '@/stores/qs-store-canvas'
import { useLayerStore } from '@/stores/qs-store-layers'
import { validateCurrentLayerOperation, hasUnlockedLayers } from '@/utils/qs-util-layerLock'
import type { KonvaStage, KonvaLayer, KonvaNode } from '@/types/qs-type-konva'
import { computed, onMounted, onUnmounted } from 'vue'

export function useDeleteMode() {
  const modeManager = useModeManager()
  const canvasStore = useCanvasStore()
  const layerStore = useLayerStore()
  const history = useHistory()

  const deleteMode = computed(() => modeManager.isDeleteMode.value)

  const hasDeletableObjects = (stage: KonvaStage): boolean => {
    const layers = stage.getLayers().filter((layer: KonvaLayer) =>
      layer.id() !== 'background' && layer.id() !== 'silhouette'
    )

    for (const layer of layers) {
      const konvaLayerId = layer.id()
      const layerData = layerStore.layers.find(l => `layer-${l.id}` === konvaLayerId)
      if (layerData?.locked) {
        continue
      }

      const children = layer.getChildren()
      for (const child of children) {
        if (child.attrs?.isOriginalPath) {
          return true
        }

        if (child.getClassName() === 'Text' && child.attrs?.isTextObject) {
          return true
        }

        if ((child.getClassName() === 'Line' || child.getClassName() === 'Group') && !child.attrs?.isOriginalPath) {
          return true
        }
      }
    }

    return false
  }

  const setupPathHoverHandlers = (child: KonvaNode, layer: KonvaLayer) => {
    child.off('mouseenter mouseleave')
    child.on('mouseenter', () => {
      child.strokeWidth(8)
      child.dash([10, 5])
      layer.batchDraw()
    })
    child.on('mouseleave', () => {
      child.strokeWidth(Math.max(child.attrs.originalStrokeWidth || 2, 2))
      child.dash([])
      layer.batchDraw()
    })
  }

  const setupTextHoverHandlers = (child: KonvaNode, layer: KonvaLayer) => {
    if (!child.attrs.originalFill) {
      child.attrs.originalFill = child.fill()
    }
    if (!child.attrs.originalStroke) {
      child.attrs.originalStroke = child.stroke()
    }
    if (!child.attrs.originalStrokeWidth) {
      child.attrs.originalStrokeWidth = child.strokeWidth()
    }

    child.off('mouseenter mouseleave')
    child.on('mouseenter', () => {
      child.fill('#dc2626')
      child.stroke('#dc2626')
      child.strokeWidth(2)
      child.shadowColor('#ef4444')
      child.shadowBlur(10)
      child.shadowOpacity(0.6)
      layer.batchDraw()
    })
    child.on('mouseleave', () => {
      child.fill('#ef4444')
      child.stroke('#ef4444')
      child.strokeWidth(1)
      child.shadowColor('')
      child.shadowBlur(0)
      child.shadowOpacity(0)
      layer.batchDraw()
    })
  }

  const activateDeleteMode = async () => {
    const validation = validateCurrentLayerOperation('delete')
    if (!validation.canProceed) {
      modeManager.switchToDrawingMode()
      return
    }

    if (!hasUnlockedLayers()) {
      modeManager.switchToDrawingMode()
      return
    }

    const stage = canvasStore.stage
    if (!stage) return

    if (!hasDeletableObjects(stage)) {
      modeManager.switchToDrawingMode()
      return
    }

    const layers = stage.getLayers().filter((layer: KonvaLayer) => layer.id() !== 'background')

    layers.forEach((layer: KonvaLayer) => {
      layer.getChildren().forEach((child: KonvaNode) => {
        if (child.attrs?.isOriginalPath || child.name() === 'original-path') {
          child.visible(true)
          child.stroke('#ef4444')
          child.strokeWidth(Math.max(child.attrs.originalStrokeWidth || 2, 2))
          child.opacity(0.8)
          child.listening(true)
          setupPathHoverHandlers(child, layer)
        } else if (child.getClassName() === 'Text' && child.attrs?.isTextObject) {
          child.visible(true)
          child.fill('#ef4444')
          child.stroke('#ef4444')
          child.strokeWidth(1)
          child.opacity(0.8)
          child.listening(true)
          setupTextHoverHandlers(child, layer)
        } else if (child.getClassName() !== 'Text') {
          child.visible(false)
        }
      })
    })

    stage.batchDraw()
  }

  const deactivateDeleteMode = async () => {
    const stage = canvasStore.stage
    if (!stage) return

    const layers = stage.getLayers().filter((layer: KonvaLayer) => layer.id() !== 'background')
    layers.forEach((layer: KonvaLayer) => {
      layer.getChildren().forEach((child: KonvaNode) => {
        if (child.attrs?.isOriginalPath) {
          child.visible(false)
          child.listening(false)
          child.off('mouseenter mouseleave')
        } else if (child.getClassName() === 'Text' && child.attrs?.isTextObject) {
          child.visible(true)
          child.listening(true)

          if (child.attrs.originalFill) {
            child.fill(child.attrs.originalFill)
            delete child.attrs.originalFill
          }
          if (child.attrs.originalStroke !== undefined) {
            child.stroke(child.attrs.originalStroke)
            delete child.attrs.originalStroke
          }
          if (child.attrs.originalStrokeWidth !== undefined) {
            child.strokeWidth(child.attrs.originalStrokeWidth)
            delete child.attrs.originalStrokeWidth
          }

          child.opacity(1)
          child.shadowColor('')
          child.shadowBlur(0)
          child.shadowOpacity(0)
          child.off('mouseenter mouseleave')
        } else {
          child.visible(true)
        }
      })
    })
    stage.batchDraw()
  }

  const deleteObjectAt = async (x: number, y: number) => {
    const stage = canvasStore.stage
    if (!stage) return false

    const pos = { x, y }
    const clickedObject = stage.getIntersection(pos) as KonvaNode | null
    if (!clickedObject) return false

    const isOriginalPath = clickedObject.attrs?.isOriginalPath || 
                          clickedObject.name() === 'original-path' ||
                          clickedObject.id().startsWith('original-path-')
    
    const isTextObject = clickedObject.getClassName() === 'Text' && clickedObject.attrs?.isTextObject
    
    if (!isOriginalPath && !isTextObject) {
      return false
    }

    const layer = clickedObject.getLayer() as KonvaLayer | null
    if (!layer) return false

    if (isTextObject) {
      const textId = clickedObject.id()
      clickedObject.destroy()
      
      try {
        const { useTextStore } = await import('@/stores/qs-store-text')
        const textStore = useTextStore()
        textStore.removeTextObject(textId)
      } catch (error) {
        console.error('Failed to remove text object from store:', error)
      }

      const layerId = layer.id().replace('layer-', '')
      const layerData = layerStore.layers.find(l => l.id === layerId)
      history.addTextEntry(`Deleted text from layer ${layerData?.name || layerId}`)
    } else {
      let visualId = clickedObject.id()
      if (visualId.startsWith('original-path-path-')) {
        visualId = visualId.replace('original-path-path-', 'path-')
      } else if (visualId.startsWith('original-path-')) {
        visualId = visualId.replace('original-path-', '')
      }
      
      const visual = layer.findOne(`#${visualId}`)
      if (visual) {
        visual.destroy()
      }

      clickedObject.destroy()

      const layerId = layer.id().replace('layer-', '')
      const pathId = visualId
      const layerData = layerStore.layers.find(l => l.id === layerId)
      layerStore.removePathFromLayer(layerId, pathId)
      history.addDeleteEntry(`Deleted object from layer ${layerData?.name || layerId}`)
    }

    stage.batchDraw()

    setTimeout(() => {
      if (!hasDeletableObjects(stage)) {
        modeManager.switchToDrawingMode()
      }
    }, 10)

    return true
  }

  onMounted(() => {
    const unsubscribe = modeManager.onModeEvent((event) => {
      if (event.mode === 'delete') {
        if (event.type === 'mode-activated') {
          activateDeleteMode()
        } else if (event.type === 'mode-deactivated') {
          deactivateDeleteMode()
        }
      }
    })

    onUnmounted(() => {
      unsubscribe()
    })
  })

  return {
    deleteMode,
    activateDeleteMode,
    deactivateDeleteMode,
    deleteObjectAt,
  }
}